import { useState } from 'react';
import PropTypes from 'prop-types';
import AnonTextInterstitial from './AnonTextInterstitial';
import AnonTextCard from './AnonTextCard';
import AnonTextShareSheet from './AnonTextShareSheet';
import IosHandoffSheet from '../../../Overlay/IosHandoffSheet';
import ConfettiBurst from './ConfettiBurst';
import { getAnonData } from '../../../../services/storage/userDataStorage';
import { buildSmsComposeUrl, buildSelfTextBody } from '../../../../services/sharing/followUpText';
import { buildPermanentUrl } from '../../../../utils/permanentUrl';
import { isIOS } from '../../../../utils/platformUtils';
import { logEvent } from '../../../../services/analytics/eventLogger';

/**
 * AnonTextFlow — the TEXT variant's Anon journey (variants block plan 2,
 * epic §8): interstitial → [Connect] → card → share sheet → post-share.
 *
 * OWNS the screen the way CARD's share wizard does (Gaps pass 1): the parent
 * renders it above the landing gate for the whole journey and suppresses the
 * sans-chat return door while it's mounted — participation flipping mid-flow
 * (the share) can never collapse the screen under the person. A reload or
 * later return visit lands on People by design (participated-returner door);
 * the retire sentinels below only need to survive the live session +
 * background-return.
 *
 * Per-room UI state (presentation-only; formation writes are the parent's,
 * unconditional):
 * - sessionStorage `text_interstitial_dismissed_` — Connect tap; a detail
 *   round-trip or refresh mid-session must not replay the interstitial
 *   (same shape as `landing_dismissed_`)
 * - localStorage `text_link_sent_` — [Text this link] retires on tap;
 *   launching Messages IS the feedback (no "✓ sent" state; a mis-tap losing
 *   the CTA is accepted)
 *
 * The self-text addresses the person's OWN just-shared number — their own
 * thread is the bookmark, and a recipient-less `sms:` link doesn't reliably
 * prefill on iOS.
 */
const AnonTextFlow = ({
  chatRoomId,
  profileData,
  connectionMetadata,
  chatMessages,
  hasSharedContactInfo,
  hostProfileUserId,
  onShareComplete,
  onSaveToPhone,
  onBack,
}) => {
  const [interstitialDismissed, setInterstitialDismissed] = useState(
    () => sessionStorage.getItem(`text_interstitial_dismissed_${chatRoomId}`) === 'true'
  );
  // null | 'name' ([Share number] fast path — name → phone) | 'select'
  // (the caret — channel selection first)
  const [sheetMode, setSheetMode] = useState(null);
  // The share flips context state asynchronously — local state makes the
  // post-share UI immediate; context covers background-return remounts.
  const [justShared, setJustShared] = useState(false);
  const [textLinkSent, setTextLinkSent] = useState(
    () => localStorage.getItem(`text_link_sent_${chatRoomId}`) === 'true'
  );
  // Save retires on tap (user ruling, device pass 1i — SUPERSEDES the epic
  // §8 "persists at every stage": declutter the main case; a completed
  // action leaves the screen)
  const [phoneSaved, setPhoneSaved] = useState(
    () => localStorage.getItem(`phone_saved_${chatRoomId}`) === 'true'
  );
  // iOS detour (user design, device pass 1j): the vCard preview's bright
  // checkmark reads as "saved" but saves nothing — an instruction sheet
  // points at "Create New Contact" first, and [Ok] opens the real vCard.
  // Android skips it (the download auto-lands in contacts). The retire
  // sentinel stays at the tap: both non-completion reasons (decided against,
  // instructed and bailed) were ruled fine to declutter.
  const [saveSheetOpen, setSaveSheetOpen] = useState(false);
  const handleSaveToPhone = () => {
    localStorage.setItem(`phone_saved_${chatRoomId}`, 'true');
    setPhoneSaved(true);
    if (isIOS()) {
      setSaveSheetOpen(true);
      return;
    }
    onSaveToPhone();
  };

  const shared = justShared || hasSharedContactInfo;

  const handleConnect = () => {
    sessionStorage.setItem(`text_interstitial_dismissed_${chatRoomId}`, 'true');
    setInterstitialDismissed(true);
  };

  // The completion beat (device pass 1 ruling): confetti bursts over the
  // card while it transforms to the post-share state — celebration +
  // orientation in one beat, no new screen.
  const [celebrating, setCelebrating] = useState(false);

  const [burstOrigin, setBurstOrigin] = useState(null);
  const handleSheetComplete = async ({ contactType, contactValue, name, tapPoint }) => {
    setSheetMode(null);
    setBurstOrigin(tapPoint || null);
    setJustShared(true);
    setCelebrating(true);
    await onShareComplete({ contactType, contactValue, name });
  };

  // The texted link: the host's permanent URL carrying the variant tag
  // (rail work step 9 — getVariantQrTag has ONE home; resolves 'text' on
  // this device because the scan tag brought us here; '' on main by lock).
  const handleTextLink = async () => {
    const link = buildPermanentUrl(hostProfileUserId);
    const anonData = await getAnonData(chatRoomId);
    localStorage.setItem(`text_link_sent_${chatRoomId}`, 'true');
    setTextLinkSent(true);
    logEvent('text_link_sent', { chatRoomId });
    // Launching Messages IS the feedback — the CTA is already retired above
    window.location.href = buildSmsComposeUrl(
      anonData?.phone || '',
      buildSelfTextBody(profileData?.name, link)
    );
  };

  if (!interstitialDismissed) {
    return (
      <AnonTextInterstitial
        chatRoomId={chatRoomId}
        profileData={profileData}
        onConnect={handleConnect}
      />
    );
  }

  return (
    <>
      <AnonTextCard
        profileData={profileData}
        connectionMetadata={connectionMetadata}
        chatMessages={chatMessages}
        shared={shared}
        celebrate={justShared}
        textLinkSent={textLinkSent}
        phoneSaved={phoneSaved}
        onShareNumber={() => setSheetMode('name')}
        onShareOptions={() => setSheetMode('select')}
        onSaveToPhone={handleSaveToPhone}
        onTextLink={handleTextLink}
        onBack={onBack}
      />
      {sheetMode && (
        <AnonTextShareSheet
          profileData={profileData}
          initialStep={sheetMode}
          onComplete={handleSheetComplete}
          onClose={() => setSheetMode(null)}
        />
      )}
      {saveSheetOpen && (
        <IosHandoffSheet
          headline="On the next screen, scroll to"
          rows={['Create New Contact', 'Add to Existing Contact']}
          onOk={() => {
            setSaveSheetOpen(false);
            onSaveToPhone();
          }}
          onClose={() => setSaveSheetOpen(false)}
        />
      )}
      {/* Completion beat (user design, device pass 1d — the redirect was
          jarring): the burst falls over the SUCCESS VIEW arriving on this
          same page; the person goes to People themselves (back button or
          "See Royale app") once they're done saving/sending. */}
      {celebrating && <ConfettiBurst origin={burstOrigin} onDone={() => setCelebrating(false)} />}
    </>
  );
};

AnonTextFlow.propTypes = {
  chatRoomId: PropTypes.string,
  profileData: PropTypes.object,
  connectionMetadata: PropTypes.object,
  chatMessages: PropTypes.array,
  hasSharedContactInfo: PropTypes.bool,
  hostProfileUserId: PropTypes.string,
  onShareComplete: PropTypes.func.isRequired,
  onSaveToPhone: PropTypes.func.isRequired,
  onBack: PropTypes.func.isRequired,
};

export default AnonTextFlow;
